import { Suspense, lazy, ComponentType } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { Card } from '@/components/ui/card';

interface LazyMobileComponentProps {
  fallback?: React.ReactNode;
  className?: string;
}

// Mobile-optimized skeleton loader
const MobileSkeleton = () => (
  <div className="space-y-4 p-4">
    <div className="flex items-center gap-3">
      <Skeleton className="h-10 w-10 rounded-full" />
      <div className="space-y-2 flex-1">
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-3 w-1/2" />
      </div>
    </div>
    <div className="grid grid-cols-2 gap-4">
      <Card className="p-4">
        <Skeleton className="h-6 w-full mb-2" />
        <Skeleton className="h-8 w-2/3" />
      </Card>
      <Card className="p-4">
        <Skeleton className="h-6 w-full mb-2" />
        <Skeleton className="h-8 w-2/3" />
      </Card>
    </div>
    <div className="space-y-3">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 p-3 border rounded-lg">
          <Skeleton className="h-8 w-8 rounded" />
          <div className="space-y-1 flex-1">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-3 w-2/3" />
          </div>
          <Skeleton className="h-6 w-16 rounded-full" />
        </div>
      ))}
    </div>
  </div>
);

// Simple lazy loading component for mobile
export const LazyComponentWrapper = ({ 
  children, 
  fallback = <MobileSkeleton /> 
}: { 
  children: React.ReactNode; 
  fallback?: React.ReactNode; 
}) => (
  <Suspense fallback={fallback}>
    {children}
  </Suspense>
);

// Performance monitoring hook
export const useMobilePerformanceMonitor = () => {
  const checkPerformance = () => {
    if (typeof window !== 'undefined' && 'performance' in window) {
      const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
      const paint = performance.getEntriesByType('paint');
      
      if (process.env.NODE_ENV === 'development') {
        console.log('Mobile Performance Metrics:', {
          domContentLoaded: navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart,
          loadComplete: navigation.loadEventEnd - navigation.loadEventStart,
          firstPaint: paint.find(p => p.name === 'first-paint')?.startTime,
          firstContentfulPaint: paint.find(p => p.name === 'first-contentful-paint')?.startTime
        });
      }
    }
  };

  return { checkPerformance };
};